import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { OgcState, OgcAddedLayer, OgcUrlType } from './ogc-state';
import { OGCServerTypeEnum, MouseProjectionEnum, SpatialReferenceEntity } from '@gishub/domain';
import { MapProvider } from '../map/map-provider';
import { OGCRepositoryProvider, SpatialReferenceRepositoryProvider } from '../../../../shared/providers/repository-providers';
import { OgcService } from './ogc-service';
import MapView from '@arcgis/core/views/MapView';
import { IdGeneratorUtil } from '../../../../shared/utils/id-generator.util';
import { CoordinateParseUtil } from '../../../../shared/utils/coordinate-parse.util';
import { projectAsync } from '@arcgis/core/geometry/projectionUtils';
import Point from '@arcgis/core/geometry/Point';

@Injectable()
export class OgcNotifier extends BaseNotifier<OgcState> {
  private static readonly INITIAL: OgcState = {
    activeSourceType: 'shapefile',
    urlType: OGCServerTypeEnum.WMS,
    url: '',
    effectiveUrl: null,
    loading: false,
    loadingMessage: null,
    lastUploadedFile: null,
    error: null,
    exampleServers: [],
    ogcLayers: [],
    ogcSearchQuery: '',
    coordinateForm: { lat: '', lng: '', easting: '', northing: '', label: '' },
    coordinateProjections: [],
    coordinateProjection: MouseProjectionEnum.Wgs84,
    addedLayers: [],
  };

  constructor(ref: Ref) {
    super(ref, OgcNotifier.INITIAL);
  }

  override async build(): Promise<void> {
    await this.loadExampleServers();
    this.loadCoordinateProjections();
  }

  private get spatialReferenceRepo() {
    return this.ref.read(SpatialReferenceRepositoryProvider).instance;
  }

  private loadCoordinateProjections(): void {
    const projections: SpatialReferenceEntity[] = this.spatialReferenceRepo.getAll();
    this.updateState(s => ({ ...s, coordinateProjections: projections }));
  }

  private async loadExampleServers(): Promise<void> {
    try {
      const res = await this.ogcRepo.getExampleServers();
      if (res.success && res.data) {
        this.updateState(s => ({ ...s, exampleServers: res.data as OgcState['exampleServers'] }));
      }
    } catch {
      // Si falla, se queda la lista vacía.
    }
  }

  async selectExampleServer(url: string): Promise<void> {
    const server = this.snapshot().exampleServers.find(s => s.url === url);
    if (!server) return;
    this.updateState(s => ({ ...s, urlType: server.type, url: server.url, error: null }));
  }

  // ─── Navegación de tipo de fuente ───────────────────────────────────────────

  setSourceType(type: OgcState['activeSourceType']): void {
    this.updateState(s => ({
      ...s,
      activeSourceType: type,
      error: null,
      ogcLayers: [],
      url: '',
      effectiveUrl: null,
      lastUploadedFile: null,
      coordinateForm: { lat: '', lng: '', easting: '', northing: '', label: '' },
      coordinateProjection: MouseProjectionEnum.Wgs84,
    }));
  }

  setUrlType(type: OgcUrlType): void {
    this.updateState(s => ({ ...s, urlType: type, url: '', effectiveUrl: null, ogcLayers: [] }));
  }

  setUrl(url: string): void {
    this.updateState(s => ({ ...s, url }));
  }

  setOgcSearchQuery(query: string): void {
    this.updateState(s => ({ ...s, ogcSearchQuery: query }));
  }

  updateCoordinateForm(fields: Partial<OgcState['coordinateForm']>): void {
    this.updateState(s => ({
      ...s,
      coordinateForm: { ...s.coordinateForm, ...fields },
    }));
  }

  setCoordinateProjection(projection: MouseProjectionEnum): void {
    this.updateState(s => ({
      ...s,
      coordinateProjection: projection,
      coordinateForm: { lat: '', lng: '', easting: '', northing: '', label: s.coordinateForm.label },
    }));
  }

  // ─── Capas OGC (URL) ─────────────────────────────────────────────────────────

  async connectOgcService(): Promise<void> {
    const state = this.snapshot();
    if (!state.url) {
      this.updateState(s => ({ ...s, error: 'Por favor, ingrese una URL válida.' }));
      return;
    }

    this.updateState(s => ({
      ...s,
      loading: true,
      loadingMessage: `Conectando al servicio ${state.urlType}...`,
      error: null,
      ogcLayers: [],
      effectiveUrl: null,
    }));

    const result = await this.ogcRepo.getCapabilities(state.url, state.urlType);
    if (!result.success || !result.data) {
      this.updateState(s => ({
        ...s,
        loading: false,
        loadingMessage: null,
        error: result.message ?? 'No se pudieron recuperar las capas del servicio.',
      }));
      return;
    }

    if (result.data.layers.length === 0) {
      this.updateState(s => ({
        ...s,
        loading: false,
        loadingMessage: null,
        error: `El servicio ${state.urlType} respondió, pero no expuso capas para esa URL. ` +
          'Verifica que la URL sea de un servicio ' + state.urlType + ' válido y con capas publicadas.',
      }));
      return;
    }

    const ogcLayers: OgcState['ogcLayers'] = result.data.layers.map(l => ({ ...l, selected: false }));
    this.updateState(s => ({
      ...s,
      ogcLayers,
      effectiveUrl: result.data!.effectiveUrl,
      loading: false,
      loadingMessage: null,
      error: null,
    }));
  }

  toggleOgcLayer(name: string): void {
    this.updateState(s => ({
      ...s,
      ogcLayers: s.ogcLayers.map(l => l.isGroup || l.name !== name ? l : { ...l, selected: !l.selected }),
    }));
  }

  toggleSelectAllOgcLayers(select: boolean): void {
    this.updateState(s => ({
      ...s,
      ogcLayers: s.ogcLayers.map(l => l.isGroup ? l : { ...l, selected: select }),
    }));
  }

  async addOgcLayersToMap(): Promise<void> {
    const state = this.snapshot();
    const view = this.ref.watch(MapProvider).state().view;
    if (!view) { this.updateState(s => ({ ...s, error: 'No hay vista de mapa activa.' })); return; }
    this.ogcSvc.setView(view as MapView);

    const selected = state.ogcLayers.filter(l => l.selected);
    if (selected.length === 0) {
      this.updateState(s => ({ ...s, error: 'Seleccione al menos una capa.' }));
      return;
    }

    // Usa la URL efectiva (directa o vía proxy) que funcionó al conectar.
    const serviceUrl = state.effectiveUrl || state.url;

    this.updateState(s => ({
      ...s,
      loading: true,
      loadingMessage: `Agregando capas ${state.urlType} al mapa...`,
      error: null,
    }));

    try {
      const hostTitle = `WMS – ${new URL(state.url).hostname}`;
      const created = await this.ogcSvc.addServiceLayers(state.urlType, serviceUrl, hostTitle, selected);

      const added: OgcAddedLayer[] = created.map(c => ({
        id: IdGeneratorUtil.generate(),
        title: c.title,
        type: c.type,
        visible: true,
        opacity: 1,
        layerRef: c.layer,
      }));

      const failedCount = selected.length - created.length;
      this.updateState(s => ({
        ...s,
        addedLayers: [...s.addedLayers, ...added],
        ogcLayers: s.ogcLayers.map(l => ({ ...l, selected: false })),
        loading: false,
        loadingMessage: null,
        error: failedCount > 0
          ? `${failedCount} capa(s) no pudieron cargarse y fueron omitidas.`
          : null,
      }));
    } catch (err: any) {
      this.updateState(s => ({
        ...s,
        loading: false,
        loadingMessage: null,
        error: `Fallo al agregar capas: ${err.message}`,
      }));
    }
  }

  // ─── Carga de archivos OGC ───────────────────────────────────────────────────
  // Todos los formatos se envían al backend `POST /api/v1/spatial/upload`
  // (multipart, campo `file`, JWT). El backend SIEMPRE responde un GeoJSON
  // FeatureCollection en WGS84. El repositorio discrimina: si el archivo es
  // GeoJSON puro se lee directamente en el frontend, sin round-trip.

  private get ogcRepo() {
    return this.ref.watch(OGCRepositoryProvider).instance;
  }

  private get ogcSvc() {
    return this.ref.watch(OgcService);
  }

  async processAndAddFile(file: File, type: OgcAddedLayer['type']): Promise<void> {
    const view = this.ref.watch(MapProvider).state().view;
    if (!view) return;
    this.ogcSvc.setView(view as MapView);

    this.updateState(s => ({
      ...s,
      loading: true,
      loadingMessage: `Subiendo ${file.name}...`,
      error: null,
      lastUploadedFile: null,
    }));
    try {
      const result = await this.ogcRepo.uploadFile(file);
      if (!result.success || !result.data) {
        throw new Error(result.message ?? 'No se pudo procesar el archivo');
      }

      const layer = await this.ogcSvc.addGeoJsonLayer(result.data.toJson(), file.name);

      this.updateState(s => ({
        ...s,
        loading: false,
        loadingMessage: null,
        error: null,
        lastUploadedFile: file.name,
        addedLayers: [...s.addedLayers, {
          id: IdGeneratorUtil.generate(), title: file.name,
          type, visible: true, opacity: 1, layerRef: layer,
        }],
      }));
    } catch (err: any) {
      this.updateState(s => ({
        ...s, loading: false, loadingMessage: null,
        error: `Fallo al procesar archivo: ${err.message || err}`,
      }));
    }
  }

  // ─── Coordenadas ─────────────────────────────────────────────────────────────

  async addCoordinatesToMap(): Promise<void> {
    const state = this.snapshot();
    const view = this.ref.watch(MapProvider).state().view;
    if (!view) return;
    this.ogcSvc.setView(view as MapView);

    const { lat: latRaw, lng: lngRaw, easting: eastingRaw, northing: northingRaw, label } = state.coordinateForm;
    const projection = state.coordinateProjection;

    let lat: number;
    let lng: number;
    if (projection === MouseProjectionEnum.Wgs84) {
      lat = CoordinateParseUtil.parseDecimal(latRaw);
      lng = CoordinateParseUtil.parseDecimal(lngRaw);
    } else {
      const wkid = this.spatialReferenceRepo.getWkidByProjection(projection);
      try {
        const easting = CoordinateParseUtil.parseDecimal(eastingRaw);
        const northing = CoordinateParseUtil.parseDecimal(northingRaw);
        if (Number.isNaN(easting) || Number.isNaN(northing)) throw new Error('UTM');
        const point = new Point({ x: easting, y: northing, spatialReference: { wkid } });
        const projected = (await projectAsync(point as any, { wkid: 4326 } as any)) as Point;
        lat = projected.latitude ?? projected.y;
        lng = projected.longitude ?? projected.x;
        if (Number.isNaN(lat) || Number.isNaN(lng)) throw new Error('UTM');
      } catch {
        this.updateState(s => ({ ...s, error: 'Ingrese Easting y Northing UTM válidos.' }));
        return;
      }
    }
    const error = CoordinateParseUtil.validateGeographic(lat, lng);
    if (error) {
      this.updateState(s => ({ ...s, error }));
      return;
    }

    try {
      const graphic = this.ogcSvc.addPoint(lat, lng, label);
      if (!graphic) return;

      const id = IdGeneratorUtil.generate();
      const ptTitle = label || `Punto (${lat.toFixed(4)}, ${lng.toFixed(4)})`;

      this.updateState(s => ({
        ...s,
        coordinateForm: { lat: '', lng: '', easting: '', northing: '', label: '' },
        error: null,
        addedLayers: [...s.addedLayers, {
          id,
          title: ptTitle,
          type: 'point',
          visible: true,
          opacity: 1,
          layerRef: graphic,   // referencia al Graphic para poder eliminarlo
        }],
      }));
    } catch (err: any) {
      this.updateState(s => ({ ...s, error: `Error: ${err.message}` }));
    }
  }

  // ─── Gestión de capas cargadas ───────────────────────────────────────────────

  toggleLayerVisibility(id: string): void {
    this.updateState(s => ({
      ...s,
      addedLayers: s.addedLayers.map(l => {
        if (l.id !== id) return l;
        const visible = !l.visible;
        // Aplicar al objeto ArcGIS si existe
        if (l.layerRef) this.ogcSvc.setVisibility(l.layerRef, visible);
        return { ...l, visible };
      }),
    }));
  }

  zoomToLayer(id: string): void {
    const layer = this.snapshot().addedLayers.find(l => l.id === id);
    if (!layer?.layerRef) return;

    if (layer.type === 'point') {
      this.ogcSvc.zoomToPoint(layer.layerRef);
    } else {
      this.ogcSvc.zoomToLayer(layer.layerRef);
    }
  }

  removeAddedLayer(id: string): void {
    const layer = this.snapshot().addedLayers.find(l => l.id === id);

    if (layer?.layerRef) {
      if (layer.type === 'point') {
        this.ogcSvc.removeGraphic(layer.layerRef);
      } else {
        this.ogcSvc.removeLayer(layer.layerRef);
      }
    }

    this.updateState(s => ({
      ...s,
      addedLayers: s.addedLayers.filter(l => l.id !== id),
    }));
  }

  setLayerOpacity(id: string, opacity: number): void {
    this.updateState(s => ({
      ...s,
      addedLayers: s.addedLayers.map(l => {
        if (l.id !== id) return l;
        if (l.layerRef && l.layerRef.opacity !== undefined) {
          this.ogcSvc.setOpacity(l.layerRef, opacity);
        }
        return { ...l, opacity };
      }),
    }));
  }
}
